import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight } from "@fortawesome/free-solid-svg-icons";

import { crmPostventaIpcApi } from "../crmPostventaIpcApi";
import { crmPostventaApi } from "../../crmpostventa/crmPostventaApi";
import { agruparClientesPorGrupo } from "../../crmpostventa/clienteGrouping";
import Alert from "../../crmpostventa-shared/components/Alert";
import Modal from "../../crmpostventa-shared/components/Modal";
import { todayIso } from "../../crmpostventa-shared/crmFormat";
import { readActiveClienteId } from "../../crmpostventa-shared/crmLinks";
import {
  SEARCH_DEBOUNCE_MS,
  SEARCH_MIN_LENGTH,
  createCorrelationId,
  normalizeClienteSearch,
} from "../../crmpostventa-rollo/nuevaGestion/nuevaGestionModel";
import ComercioStep from "./ComercioStep";
import GestionIpcStep, { ComercioHeader, ContextoError, ContextoLoading } from "./GestionIpcStep";
import {
  activoKey,
  buildActivoCard,
  buildComercioCards,
  buildCrearPayload,
  buildInitialForm,
  buildToast,
  changeMotivoProactiva,
  changeTipo,
  describeCreateError,
  fieldId,
  findSimilar,
  firstErrorKey,
  isActivoKey,
  isProactivaCategoria,
  observacionObligatoria,
  terminalesDeContexto,
  validateGestionForm,
} from "./nuevaGestionIpcModel";

const TITLE_ID = "crm-ipc-nueva-title";
const IDLE_SEARCH = { status: "idle", results: [], text: "" };
const IDLE_CONTEXTO = { status: "idle", data: null, error: null, clienteId: null };
const IDLE_ACTIVO = { status: "idle", card: null };

const withoutKeys = (errors, keys) => {
  if (!keys.some((key) => errors[key])) return errors;
  const next = { ...errors };
  keys.forEach((key) => delete next[key]);
  return next;
};

/**
 * "Nueva gestión" of Gestiones IPC (design Prompt 2 for IPC, Prompt 3 for Proactiva, Prompts 6 and 7): step 1 picks a
 * comercio with the CRM client search (the CRM active comercio first in "Comercios recientes"), step 2 loads GET
 * contexto of that alta, shows its gestiones and creates the gestión with POST gestiones.
 *
 * `tipo` preloads the IPC type (of the section it was opened from, or of the alta link). `start` ({ clienteId,
 * numeroTerminal }, from "Registrar gestión" in the Ficha) opens step 2 with that alta's comercio and terminal;
 * "Cambiar comercio" still goes back to step 1. When the active comercio cannot be loaded, step 1 offers "Reintentar".
 * onCreated({ gestionId, categoria, tipo, finalizada, toast }) runs after a successful POST and onViewOpen({ gestion,
 * cuit }) when the asesor asks to see a similar open gestión. Both are expected to close the modal.
 */
export default function NuevaGestionIpcModal({
  categoria,
  tipo = null,
  start = null,
  darkMode = false,
  asesorNombre,
  returnFocusRef,
  onClose,
  onCreated,
  onViewOpen,
}) {
  const proactiva = isProactivaCategoria(categoria);
  const [registro] = useState(() => new Date());
  const [step, setStep] = useState(start ? 2 : 1);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState(IDLE_SEARCH);
  const [activo, setActivo] = useState(IDLE_ACTIVO);
  const [selectedKey, setSelectedKey] = useState(null);
  // Comercio of step 2: its card, shown in the header until the contexto answers.
  const [comercio, setComercio] = useState(start ? { key: activoKey(start.clienteId), clienteId: start.clienteId } : null);
  const [contexto, setContexto] = useState(IDLE_CONTEXTO);
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ack, setAck] = useState(false);
  const [prioridadOpen, setPrioridadOpen] = useState(false);
  const [focusRequest, setFocusRequest] = useState(0);
  const searchRequestRef = useRef(0);
  const contextoRequestRef = useRef(0);
  const attemptRef = useRef(null);
  const mountedRef = useRef(true);
  const searchInputRef = useRef(null);
  const focusFormRef = useRef(false);
  const today = todayIso();

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  // ---- Step 1: debounced search; only the latest request may update the results.
  const runSearch = useCallback(async (text) => {
    searchRequestRef.current += 1;
    const requestId = searchRequestRef.current;
    setSearch((current) => ({ ...current, status: "loading" }));
    try {
      const data = await crmPostventaApi.buscarClientes(normalizeClienteSearch(text));
      if (!mountedRef.current || requestId !== searchRequestRef.current) return;
      const results = buildComercioCards(agruparClientesPorGrupo(Array.isArray(data) ? data : []));
      setSearch({ status: "done", results, text });
      setSelectedKey((current) => (results.some((card) => card.key === current) ? current : null));
    } catch (searchError) {
      if (!mountedRef.current || requestId !== searchRequestRef.current) return;
      setSearch({ status: "error", results: [], text });
    }
  }, []);

  useEffect(() => {
    const text = query.trim();
    searchRequestRef.current += 1;
    if (text.length < SEARCH_MIN_LENGTH) {
      setSearch({ ...IDLE_SEARCH, text });
      setSelectedKey((current) => (isActivoKey(current) ? current : null));
      return undefined;
    }

    setSearch((current) => ({ ...current, status: "loading" }));
    const handle = setTimeout(() => runSearch(text), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query, runSearch]);

  // "Comercios recientes": the CRM active comercio, read once when step 1 first shows (Prompt 7, point 4).
  useEffect(() => {
    if (step !== 1 || activo.status !== "idle") return;
    const clienteId = readActiveClienteId();
    if (!clienteId) {
      setActivo({ status: "none", card: null });
      return;
    }
    setActivo({ status: "loading", card: null });
    crmPostventaIpcApi
      .obtenerContexto(clienteId)
      .then((data) => {
        if (mountedRef.current) setActivo({ status: "done", card: buildActivoCard(data) });
      })
      .catch(() => {
        if (mountedRef.current) setActivo({ status: "error", card: null });
      });
  }, [step, activo.status]);

  useEffect(() => {
    if (step === 1) searchInputRef.current?.focus();
  }, [step]);

  // "Reintentar" of the active comercio: back to idle, which makes the "Comercios recientes" effect above run again
  // (it only loads while activo.status is "idle" and step 1 shows).
  const retryActivo = () => setActivo(IDLE_ACTIVO);

  // ---- Step 2: contexto of the chosen alta. A new comercio starts a fresh form; the terminal preloaded from the
  // Ficha only applies to the Ficha's comercio.
  const applyContexto = useCallback(
    (clienteId, data) => {
      setContexto({ status: "done", data: data || {}, error: null, clienteId });
      setForm(
        buildInitialForm({
          categoria,
          tipo,
          terminales: terminalesDeContexto(data),
          terminalPrecargada: start && start.clienteId === clienteId ? start.numeroTerminal : null,
          now: new Date(),
        })
      );
      setErrors({});
      setSubmitError(null);
      setAck(false);
      setPrioridadOpen(false);
      attemptRef.current = null;
      focusFormRef.current = true;
    },
    [categoria, tipo, start]
  );

  const loadContexto = useCallback(
    async (clienteId) => {
      contextoRequestRef.current += 1;
      const requestId = contextoRequestRef.current;
      setContexto({ ...IDLE_CONTEXTO, status: "loading", clienteId });
      setForm(null);
      try {
        const data = await crmPostventaIpcApi.obtenerContexto(clienteId);
        if (!mountedRef.current || requestId !== contextoRequestRef.current) return;
        applyContexto(clienteId, data);
      } catch (loadError) {
        if (!mountedRef.current || requestId !== contextoRequestRef.current) return;
        setContexto({ status: "error", data: null, error: loadError, clienteId });
      }
    },
    [applyContexto]
  );

  useEffect(() => {
    if (start) loadContexto(start.clienteId);
    // Only the comercio the modal was opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cardOf = (key) => (key && activo.card?.key === key ? activo.card : search.results.find((card) => card.key === key)) || null;

  const goToStep2 = (key) => {
    const card = cardOf(key);
    if (!card) return;
    setSelectedKey(key);
    setStep(2);
    const sameComercio = comercio?.clienteId === card.clienteId && contexto.status === "done" && contexto.clienteId === card.clienteId;
    setComercio(card);
    if (sameComercio && form) return;
    if (card.contexto) {
      contextoRequestRef.current += 1;
      applyContexto(card.clienteId, card.contexto);
      return;
    }
    loadContexto(card.clienteId);
  };

  const backToStep1 = () => {
    contextoRequestRef.current += 1;
    if (contexto.status === "loading") setContexto(IDLE_CONTEXTO);
    setSelectedKey(comercio?.key ?? null);
    setStep(1);
  };

  // Focus: the first control once the form shows; after a failed submit, the first invalid field.
  useEffect(() => {
    if (step !== 2 || !form || !focusFormRef.current) return;
    focusFormRef.current = false;
    document.getElementById(fieldId(proactiva ? "motivo" : "tipo"))?.focus();
  }, [step, form, proactiva]);

  useEffect(() => {
    if (!focusRequest) return;
    const key = firstErrorKey(errors);
    if (key) document.getElementById(fieldId(key))?.focus();
    // Only a new submit moves the focus, not every error change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest]);

  const nextFormFor = (key, value) => {
    switch (key) {
      case "tipo":
        return changeTipo(form, value);
      case "motivo":
        return proactiva ? changeMotivoProactiva(form, value, new Date()) : { ...form, motivo: value };
      case "proximoContacto":
        return { ...form, proximoContacto: value, proximoAutomatico: false };
      case "mostrarCanal":
        return { ...form, mostrarCanal: value, canal: value ? form.canal : "" };
      default:
        return { ...form, [key]: value };
    }
  };

  const changeField = (key, value) => {
    if (!form) return;
    const next = nextFormFor(key, value);
    setForm(next);
    if (key === "tipo" || key === "motivo") setAck(false);
    if (key === "tipo" || key === "prioridad") setPrioridadOpen(false);
    const cleared = [key];
    if (key === "tipo") cleared.push("motivo");
    if (key === "mostrarCanal") cleared.push("canal");
    if (key === "estado") cleared.push("proximoContacto", "motivoCierre");
    if ((key === "tipo" || key === "motivo") && !observacionObligatoria(categoria, next)) cleared.push("observacion");
    setErrors((current) => withoutKeys(current, cleared));
  };

  // One correlationId per attempt: a retry of the same data (after a network error, for example) reuses it, so the
  // server answers the gestión it may already have created instead of creating a second one (RN5).
  const submit = async () => {
    if (saving || !form || contexto.status !== "done") return;
    const nextErrors = validateGestionForm(form, categoria, new Date());
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setFocusRequest((count) => count + 1);
      return;
    }

    const payload = buildCrearPayload({ clienteId: contexto.clienteId, categoria, form });
    const key = JSON.stringify(payload);
    if (attemptRef.current?.key !== key) {
      attemptRef.current = { key, correlationId: createCorrelationId() };
    }

    setSaving(true);
    setSubmitError(null);
    try {
      const response = await crmPostventaIpcApi.crearGestion({ ...payload, correlationId: attemptRef.current.correlationId });
      if (!mountedRef.current) return;
      const gestion = response?.gestion;
      onCreated?.({
        gestionId: gestion?.id ?? null,
        categoria: payload.categoria,
        tipo: payload.tipo,
        finalizada: gestion ? gestion.activo === false : payload.estado === "RESUELTA",
        toast: buildToast(contexto.data?.cliente || comercio, payload),
      });
    } catch (createError) {
      if (!mountedRef.current) return;
      setSaving(false);
      setSubmitError(describeCreateError(createError));
    }
  };

  const formReady = step === 2 && contexto.status === "done" && Boolean(form);
  const similar = formReady ? findSimilar(contexto.data?.gestionesAbiertas, categoria, form) : null;

  const viewSimilar = () =>
    onViewOpen?.({ gestion: similar, cuit: contexto.data?.cliente?.cuit || comercio?.cuit || "" });

  const renderStep2Body = () => {
    if (contexto.status === "error") {
      return <ContextoError message={contexto.error?.message} onRetry={() => loadContexto(comercio.clienteId)} />;
    }
    if (!formReady) return <ContextoLoading />;
    return (
      <GestionIpcStep
        categoria={categoria}
        contexto={contexto.data}
        form={form}
        errors={errors}
        saving={saving}
        similar={similar}
        ack={ack}
        prioridadOpen={prioridadOpen}
        asesorNombre={asesorNombre}
        registro={registro}
        today={today}
        onChange={changeField}
        onOpenPrioridad={() => setPrioridadOpen(true)}
        onViewSimilar={viewSimilar}
        onAckSimilar={() => setAck(true)}
        onSubmit={submit}
      />
    );
  };

  const renderStep2 = () => (
    <>
      {submitError ? (
        <div className="crm-ipc__submit-error">
          <Alert tone="error" role="alert" title={submitError.title}>
            {submitError.text}
          </Alert>
          <div>
            <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" disabled={saving} onClick={submit}>
              <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
              Reintentar
            </button>
          </div>
        </div>
      ) : null}
      <ComercioHeader
        comercio={(contexto.status === "done" && contexto.data?.cliente) || comercio}
        terminal={proactiva && formReady ? form.terminal : null}
        disabled={saving}
        onChangeComercio={backToStep1}
      />
      {renderStep2Body()}
    </>
  );

  const cancelButton = (
    <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary" disabled={saving} onClick={onClose}>
      Cancelar
    </button>
  );

  const renderFooter = () => {
    if (step === 1) {
      const selectable = Boolean(cardOf(selectedKey));
      return (
        <>
          <span className="crm-rollo__grow">{selectable ? "" : "Elegí un comercio para continuar"}</span>
          {cancelButton}
          <button type="button" className="crm-rollo__btn crm-rollo__btn--primary" disabled={!selectable} onClick={() => goToStep2(selectedKey)}>
            Seleccionar
          </button>
        </>
      );
    }

    return (
      <>
        <span className="crm-rollo__grow">
          <span className="crm-rollo__req" aria-hidden="true">
            *
          </span>{" "}
          Obligatorio
        </span>
        {cancelButton}
        <button
          type="button"
          className="crm-rollo__btn crm-rollo__btn--primary"
          disabled={saving || !formReady}
          aria-busy={saving || undefined}
          onClick={submit}
        >
          {saving ? (
            <>
              <span className="crm-rollo__spinner" aria-hidden="true" />
              Guardando…
            </>
          ) : (
            "Guardar gestión"
          )}
        </button>
      </>
    );
  };

  return (
    <Modal
      titleId={TITLE_ID}
      title={step === 1 ? "Seleccionar comercio" : "Datos de la gestión"}
      stepLabel={`${proactiva ? "Nueva gestión proactiva" : "Nueva gestión IPC"} · Paso ${step} de 2`}
      steps={2}
      step={step}
      size={step === 1 ? "sm" : "md"}
      className="crm-ipc"
      darkMode={darkMode}
      closeDisabled={saving}
      returnFocusRef={returnFocusRef}
      onClose={onClose}
      footer={renderFooter()}
    >
      {step === 1 ? (
        <ComercioStep
          proactiva={proactiva}
          inputRef={searchInputRef}
          query={query}
          onQueryChange={setQuery}
          search={search}
          activo={activo}
          selectedKey={selectedKey}
          onSelect={setSelectedKey}
          onConfirm={goToStep2}
          onRetry={() => runSearch(query.trim())}
          onRetryActivo={retryActivo}
        />
      ) : (
        renderStep2()
      )}
    </Modal>
  );
}
